<template>
    <div
        class="parallax-area">
        <div
            ref="inner"
            class="parallax-inner">
            <slot></slot>
        </div>
    </div>
</template>

<script>
    /**
     * ParallaxArea is used to wrap some content that you want to apply a
     * parallax effect to when scrolling.
     *
     * The effect is based on where the element is positioned vertically within
     * the viewport. While at the top of the viewport, the bottom of the inner
     * contents are visible, and vice versa.
     *
     * To use:
     *  1) Write the html:
     *       <ParallaxArea>
     *           <imgOrOtherContent>
     *       </ParallaxArea>
     *  2) Position and size the parallax area through CSS; at a minimum, you
     *     must apply a height (either directly, or through flexbox, etc.)
     *  3) Size the inner area. You don't need to target `.parallax-inner`
     *     specifically; sizing the image or other inner content will work just
     *     fine.
     *  4) Optionally, call `update()` if the inner content is dynamically
     *     loaded (or the inner size cannot be computed at mount-time).
     *
     * Code example: see ForumCategory.vue
     */

    export default {
        data() {
            return {
                elemTop: null,
                elemHeight: null,
                elemInnerHeight: null,
                viewTop: null,
                viewHeight: null,
                requestId: null,
            };
        },

        mounted() {
            this.update();
            window.addEventListener('resize', this.onResize);
            window.addEventListener('scroll', this.onScroll);
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
            window.removeEventListener('scroll', this.onScroll);
            window.cancelAnimationFrame(this.requestId);
        },

        methods: {
            onResize() {
                this.updateStoredValues();
                this.scheduleUpdateScroll();
            },

            onScroll() {
                this.viewTop = window.scrollY;
                this.scheduleUpdateScroll();
            },

            scheduleUpdateScroll() {
                if (this.requestId === null) {
                    const cb = this.updateScroll;
                    this.requestId = window.requestAnimationFrame(cb);
                }
            },

            /**
             * If you want to make sure that everything is up-to-date after
             * changing some sizes, finishing loading, etc. call this.
             */
            update() {
                this.updateStoredValues();
                this.scheduleUpdateScroll();
            },

            updateScroll() {
                const maxDiff = this.viewHeight + this.elemHeight;
                const diff = (this.viewTop + this.viewHeight) - this.elemTop;
                const relDiff = Math.max(0, Math.min(1, diff / maxDiff));
                const scrollTop = (relDiff) * this.elemInnerHeight;
                this.$el.scrollTop = scrollTop;
                this.requestId = null;
            },

            updateStoredValues() {
                const innerHeight = $(this.$refs.inner).height();
                this.elemTop = $(this.$el).offset().top;
                this.elemHeight = $(this.$el).height();
                this.elemInnerHeight = innerHeight - this.elemHeight;
                this.viewTop = $(window).scrollTop();
                this.viewHeight = $(window).height();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .parallax-area {
        overflow-x: hidden;
        overflow-y: auto;
        perspective: 1px;
        pointer-events: none;
    }

    .parallax-inner {
        transform: translateZ(-1px) scale(2);
        pointer-events: auto;
    }

    // Hide scrollbar for various browsers
    .parallax-area {
        scrollbar-width: none; // Firefox
        -ms-overflow-style: none; // IE 10+
        &::-webkit-scrollbar { // WebKit
            width: 0;
        }
    }
</style>



// WEBPACK FOOTER //
// index.vue?df31ebce